import { request, setAuth, clearAuth,
  forgetTeamBrowser } from './client'
import type {
  AdminDayAvailability,
  BookingBalance,
  DashboardSummary,
  FeedbackDto,
  OccupancyPoint,
  OrderStatus,
  PaymentDto,
  RevenueBreakdown,
  ServiceCategory,
  ServiceItemDto,
  ServiceItemRequest,
  ServiceOrderDto,
  ServiceOrderPayload,
  NoteStatus,
  Requester,
  GalleryPage,
  GallerySubject,
  MediaAssetDto,
  MediaCategory,
  MediaSeason,
  SiteNoteDto,
  ExperienceRequest,
  TestimonialDto,
  UserDto,
  UserRole,
  AttractionCategory,
  AttractionDto,
  AuthResponse,
  AvailabilityCalendar,
  BookingRequestPayload,
  BookingResponse,
  BookingStatus,
  CalendarUpdatePayload,
  CurrentUser,
  DestinationDetail,
  DestinationSummary,
  HotelDto,
  IcalFeedResponse,
  IsoDate,
  LanguageDto,
  Page,
  PriceBand,
  PublicBookingResponse,
  QuoteResponse,
  Season,
  SeasonalActivityDto,
  SeasonalActivityRequestPayload,
  UnitDetail,
  UnitSummary,
} from '../types/api'

/**
 * Every endpoint the app calls, in one place.
 *
 * Components never build URLs. When the API version moves or a query parameter is renamed,
 * this file is the only thing that changes.
 */

// ---------------------------------------------------------------- public: units

export const unitsApi = {
  list: (locale: string) => request<UnitSummary[]>('/api/v1/units', { locale }),

  get: (slug: string, locale: string) => request<UnitDetail>(`/api/v1/units/${slug}`, { locale }),

  /**
   * @param from first night, inclusive
   * @param to   last night, exclusive
   */
  availability: (slug: string, from: IsoDate, to: IsoDate) =>
    request<AvailabilityCalendar>(`/api/v1/units/${slug}/availability`, { query: { from, to } }),

  quote: (slug: string, checkIn: IsoDate, checkOut: IsoDate, adults: number, children: number) =>
    request<QuoteResponse>(`/api/v1/units/${slug}/quote`, {
      method: 'POST',
      body: { checkIn, checkOut, adults, children },
    }),
}

// ---------------------------------------------------------------- public: bookings

export const bookingsApi = {
  create: (unitSlug: string, payload: BookingRequestPayload) =>
    request<BookingResponse>('/api/v1/bookings', {
      method: 'POST',
      body: payload,
      query: { unit: unitSlug },
    }),

  lookup: (reference: string, email: string) =>
    request<PublicBookingResponse>(`/api/v1/bookings/reference/${encodeURIComponent(reference)}`, {
      query: { email },
    }),
}

// ---------------------------------------------------------------- public: content

export const contentApi = {
  testimonials: (locale: string) =>
    request<TestimonialDto[]>('/api/v1/testimonials', { locale }),

  submitExperience: (payload: ExperienceRequest) =>
    request<void>('/api/v1/testimonials', { method: 'POST', body: payload }),

  languages: () => request<LanguageDto[]>('/api/v1/site/languages'),

  destinations: (locale: string) => request<DestinationSummary[]>('/api/v1/destinations', { locale }),

  destination: (slug: string, locale: string) =>
    request<DestinationDetail>(`/api/v1/destinations/${slug}`, { locale }),

  attractions: (
    locale: string,
    filters: { destination?: string; category?: AttractionCategory; season?: Season } = {},
  ) => request<AttractionDto[]>('/api/v1/attractions', { locale, query: filters }),

  featuredAttractions: (locale: string) =>
    request<AttractionDto[]>('/api/v1/attractions/featured', { locale }),

  attraction: (slug: string, locale: string) =>
    request<AttractionDto>(`/api/v1/attractions/${slug}`, { locale }),

  activities: (locale: string, season?: Season) =>
    request<SeasonalActivityDto[]>('/api/v1/seasonal-activities', { locale, query: { season } }),

  currentSeason: () => request<Season>('/api/v1/seasonal-activities/current-season'),

  hotels: (locale: string, filters: { destination?: string; priceBand?: PriceBand } = {}) =>
    request<HotelDto[]>('/api/v1/hotels', { locale, query: filters }),
}

// ---------------------------------------------------------------- admin: auth

export const authApi = {
  async login(email: string, password: string): Promise<CurrentUser> {
    const response = await request<AuthResponse>('/api/v1/auth/login', {
      method: 'POST',
      body: { email, password },
    })
    setAuth(response.accessToken, response.refreshToken, response.expiresInSeconds)
    return response.user
  },

  me: () => request<CurrentUser>('/api/v1/auth/me', { authenticated: true }),

  logout(): void {
    clearAuth()
    forgetTeamBrowser()
  },
}

// ---------------------------------------------------------------- admin: operations

export const adminApi = {
  units: (locale: string) => request<UnitSummary[]>('/api/v1/admin/units', { locale, authenticated: true }),

  unit: (id: string, locale: string) =>
    request<UnitDetail>(`/api/v1/admin/units/${id}`, { locale, authenticated: true }),

  rotateIcalToken: (id: string) =>
    request<{ icalToken: string }>(`/api/v1/admin/units/${id}/rotate-ical-token`, {
      method: 'POST',
      authenticated: true,
    }),

  calendar: (unitId: string, from: IsoDate, to: IsoDate) =>
    request<AdminDayAvailability[]>(`/api/v1/admin/calendar/units/${unitId}`, {
      query: { from, to },
      authenticated: true,
    }),

  updateCalendar: (unitId: string, payload: CalendarUpdatePayload) =>
    request<{ unitId: string; from: string; to: string; nightsUpdated: number }>(
      `/api/v1/admin/calendar/units/${unitId}`,
      { method: 'PUT', body: payload, authenticated: true },
    ),

  bookings: (filters: { status?: BookingStatus; unitId?: string; page?: number; size?: number } = {}) =>
    request<Page<BookingResponse>>('/api/v1/admin/bookings', {
      query: filters,
      authenticated: true,
    }),

  booking: (id: string) => request<BookingResponse>(`/api/v1/admin/bookings/${id}`, { authenticated: true }),

  updateBookingStatus: (id: string, status: BookingStatus, ownerNote?: string) =>
    request<BookingResponse>(`/api/v1/admin/bookings/${id}/status`, {
      method: 'PATCH',
      body: { status, ownerNote },
      authenticated: true,
    }),

  icalFeeds: () => request<IcalFeedResponse[]>('/api/v1/admin/calendar/ical-feeds', { authenticated: true }),

  syncIcalFeed: (id: string) =>
    request<IcalFeedResponse>(`/api/v1/admin/calendar/ical-feeds/${id}/sync`, {
      method: 'POST',
      authenticated: true,
    }),

  createIcalFeed: (payload: {
    unitId: string
    name: string
    url: string
    direction: 'IMPORT' | 'EXPORT'
    active: boolean
  }) =>
    request<IcalFeedResponse>('/api/v1/admin/calendar/ical-feeds', {
      method: 'POST',
      body: payload,
      authenticated: true,
    }),

  deleteIcalFeed: (id: string) =>
    request<void>(`/api/v1/admin/calendar/ical-feeds/${id}`, { method: 'DELETE', authenticated: true }),

  activities: (locale: string) =>
    request<SeasonalActivityDto[]>('/api/v1/admin/seasonal-activities', { locale, authenticated: true }),

  activity: (id: string, locale: string) =>
    request<SeasonalActivityDto>(`/api/v1/admin/seasonal-activities/${id}`, {
      locale,
      authenticated: true,
    }),

  createActivity: (payload: SeasonalActivityRequestPayload) =>
    request<SeasonalActivityDto>('/api/v1/admin/seasonal-activities', {
      method: 'POST',
      body: payload,
      authenticated: true,
    }),

  updateActivity: (id: string, payload: SeasonalActivityRequestPayload) =>
    request<SeasonalActivityDto>(`/api/v1/admin/seasonal-activities/${id}`, {
      method: 'PUT',
      body: payload,
      authenticated: true,
    }),

  deleteActivity: (id: string) =>
    request<void>(`/api/v1/admin/seasonal-activities/${id}`, { method: 'DELETE', authenticated: true }),

  attractions: (locale: string) =>
    request<AttractionDto[]>('/api/v1/admin/attractions', { locale, authenticated: true }),

  hotels: (locale: string) => request<HotelDto[]>('/api/v1/admin/hotels', { locale, authenticated: true }),

  destinations: (locale: string) =>
    request<DestinationSummary[]>('/api/v1/admin/destinations', { locale, authenticated: true }),
}

// ---------------------------------------------------------------- services and extras

export const servicesApi = {
  menu: (locale: string, category?: ServiceCategory) =>
    request<ServiceItemDto[]>('/api/v1/services', { locale, query: { category } }),

  orderable: (locale: string) => request<ServiceItemDto[]>('/api/v1/services/orderable', { locale }),

  placeOrder: (payload: ServiceOrderPayload) =>
    request<ServiceOrderDto>('/api/v1/service-orders', { method: 'POST', body: payload }),

  myOrders: (reference: string, email: string) =>
    request<ServiceOrderDto[]>('/api/v1/service-orders', { query: { reference, email } }),

  cancelOrder: (reference: string, email: string) =>
    request<ServiceOrderDto>(`/api/v1/service-orders/${encodeURIComponent(reference)}/cancel`, {
      method: 'POST',
      query: { email },
    }),

  balance: (reference: string, email: string) =>
    request<BookingBalance>(`/api/v1/bookings/${encodeURIComponent(reference)}/balance`, {
      query: { email },
    }),

  reviews: () => request<FeedbackDto[]>('/api/v1/reviews'),

  submitReview: (payload: {
    bookingReference: string
    guestEmail: string
    rating: number
    comment?: string
    displayName?: string
  }) => request<FeedbackDto>('/api/v1/reviews', { method: 'POST', body: payload }),

  /** Owner review notes, left on the staging build. */
  addSiteNote: (payload: {
    pagePath: string
    locale?: string
    note: string
    authorName?: string
    requester?: Requester
    teamToken?: string
  }) => request<SiteNoteDto>('/api/v1/site-notes', { method: 'POST', body: payload }),
}

// ---------------------------------------------------------------- admin operations

export const opsApi = {
  dashboard: () => request<DashboardSummary>('/api/v1/admin/ops/dashboard', { authenticated: true }),

  occupancy: () =>
    request<OccupancyPoint[]>('/api/v1/admin/ops/dashboard/occupancy', { authenticated: true }),

  revenue: (months = 12) =>
    request<RevenueBreakdown[]>('/api/v1/admin/ops/dashboard/revenue', {
      query: { months },
      authenticated: true,
    }),

  services: (locale: string) =>
    request<ServiceItemDto[]>('/api/v1/admin/ops/services', { locale, authenticated: true }),

  createService: (payload: ServiceItemRequest) =>
    request<ServiceItemDto>('/api/v1/admin/ops/services', {
      method: 'POST',
      body: payload,
      authenticated: true,
    }),

  updateService: (id: string, payload: ServiceItemRequest) =>
    request<ServiceItemDto>(`/api/v1/admin/ops/services/${id}`, {
      method: 'PUT',
      body: payload,
      authenticated: true,
    }),

  /** Hidden, not deleted: past orders still point at it. */
  deactivateService: (id: string) =>
    request<void>(`/api/v1/admin/ops/services/${id}`, { method: 'DELETE', authenticated: true }),

  openOrders: () => request<ServiceOrderDto[]>('/api/v1/admin/ops/orders/open', { authenticated: true }),

  orders: (status?: OrderStatus) =>
    request<ServiceOrderDto[]>('/api/v1/admin/ops/orders', { query: { status }, authenticated: true }),

  updateOrderStatus: (id: string, status: OrderStatus, ownerNote?: string) =>
    request<ServiceOrderDto>(`/api/v1/admin/ops/orders/${id}/status`, {
      method: 'PATCH',
      query: { status, ownerNote },
      authenticated: true,
    }),

  balance: (bookingId: string) =>
    request<BookingBalance>(`/api/v1/admin/ops/bookings/${bookingId}/balance`, { authenticated: true }),

  recordPayment: (
    bookingId: string,
    payload: { amount: number; method: string; paidAt?: string; note?: string },
  ) =>
    request<PaymentDto>(`/api/v1/admin/ops/bookings/${bookingId}/payments`, {
      method: 'POST',
      body: payload,
      authenticated: true,
    }),

  deletePayment: (paymentId: string) =>
    request<void>(`/api/v1/admin/ops/payments/${paymentId}`, {
      method: 'DELETE',
      authenticated: true,
    }),

  reviews: () => request<FeedbackDto[]>('/api/v1/admin/ops/reviews', { authenticated: true }),

  replyToReview: (id: string, reply: string) =>
    request<FeedbackDto>(`/api/v1/admin/ops/reviews/${id}/reply`, {
      method: 'PATCH',
      query: { reply },
      authenticated: true,
    }),

  publishReview: (id: string, published: boolean) =>
    request<FeedbackDto>(`/api/v1/admin/ops/reviews/${id}/published`, {
      method: 'PATCH',
      query: { published },
      authenticated: true,
    }),

  siteNotes: () => request<SiteNoteDto[]>('/api/v1/admin/ops/site-notes', { authenticated: true }),

  replyToSiteNote: (id: string, body: string) =>
    request<SiteNoteDto>(`/api/v1/admin/ops/site-notes/${id}/replies`, {
      method: 'POST',
      query: { body },
      authenticated: true,
    }),

  setSiteNoteStatus: (id: string, status: NoteStatus) =>
    request<SiteNoteDto>(`/api/v1/admin/ops/site-notes/${id}/status`, {
      method: 'PATCH',
      query: { status },
      authenticated: true,
    }),

  resolveSiteNote: (id: string, resolved: boolean) =>
    request<SiteNoteDto>(`/api/v1/admin/ops/site-notes/${id}`, {
      method: 'PATCH',
      query: { resolved },
      authenticated: true,
    }),

  /**
   * Moves a note to the bin. Reversible: the note comes back with restoreSiteNote, exactly as
   * it was. Returns the note so the console can offer Undo without fetching again.
   */
  deleteSiteNote: (id: string) =>
    request<SiteNoteDto>(`/api/v1/admin/ops/site-notes/${id}`, {
      method: 'DELETE',
      authenticated: true,
    }),

  restoreSiteNote: (id: string) =>
    request<SiteNoteDto>(`/api/v1/admin/ops/site-notes/${id}/restore`, {
      method: 'POST',
      authenticated: true,
    }),

  /** The bin, most recently deleted first. */
  deletedSiteNotes: () =>
    request<SiteNoteDto[]>('/api/v1/admin/ops/site-notes/deleted', { authenticated: true }),
}

// ---------------------------------------------------------------- picture library

/**
 * The public gallery.
 *
 * Paged rather than fetched whole: there are several hundred photographs and a phone should
 * download fifteen of them, not all of them, to look at the first row.
 */
export const galleryApi = {
  page: (
    category: MediaCategory | undefined,
    page: number,
    size = 15,
    kind?: 'IMAGE' | 'VIDEO',
  ) => request<GalleryPage>('/api/v1/gallery', { query: { category, kind, page, size } }),

  subjects: () => request<GallerySubject[]>('/api/v1/gallery/subjects'),

  /** How many films there are. Zero means the Films tab is not drawn at all. */
  films: () => request<number>('/api/v1/gallery/films'),
}

export const mediaApi = {
  list: (category?: MediaCategory) =>
    request<MediaAssetDto[]>('/api/v1/admin/media', {
      query: { category },
      authenticated: true,
    }),

  upload: (file: File, category: MediaCategory, season?: MediaSeason, altText?: string) => {
    const form = new FormData()
    form.append('file', file)
    return request<MediaAssetDto>('/api/v1/admin/media', {
      method: 'POST',
      // Subject and alt text ride as query parameters rather than form fields: the endpoint
      // takes one @RequestPart, and mixing parts and params keeps the Java side simple.
      query: { category, season, altText },
      body: form,
      authenticated: true,
    })
  },

  describe: (id: string, altText: string, category?: MediaCategory, season?: MediaSeason) =>
    request<MediaAssetDto>(`/api/v1/admin/media/${id}`, {
      method: 'PATCH',
      query: { altText, category, season },
      authenticated: true,
    }),

  /** Show or hide one picture on the public gallery. */
  setVisible: (id: string, visible: boolean) =>
    request<MediaAssetDto>(`/api/v1/admin/media/${id}`, {
      method: 'PATCH',
      query: { visible },
      authenticated: true,
    }),

  remove: (id: string) =>
    request<void>(`/api/v1/admin/media/${id}`, { method: 'DELETE', authenticated: true }),
}

// ---------------------------------------------------------------- console accounts

export const testimonialsApi = {
  all: (locale: string) =>
    request<TestimonialDto[]>('/api/v1/admin/testimonials', { locale, authenticated: true }),

  setPublished: (id: string, published: boolean) =>
    request<TestimonialDto>(`/api/v1/admin/testimonials/${id}/published`, {
      method: 'PATCH',
      query: { published },
      authenticated: true,
    }),

  setRating: (id: string, rating: number) =>
    request<TestimonialDto>(`/api/v1/admin/testimonials/${id}/rating`, {
      method: 'PATCH',
      query: { rating },
      authenticated: true,
    }),

  remove: (id: string) =>
    request<void>(`/api/v1/admin/testimonials/${id}`, { method: 'DELETE', authenticated: true }),
}

export const usersApi = {
  list: () => request<UserDto[]>('/api/v1/admin/users', { authenticated: true }),

  create: (payload: { email: string; fullName: string; role: UserRole; password: string }) =>
    request<UserDto>('/api/v1/admin/users', {
      method: 'POST',
      body: payload,
      authenticated: true,
    }),

  setEnabled: (id: string, enabled: boolean) =>
    request<UserDto>(`/api/v1/admin/users/${id}/enabled`, {
      method: 'PATCH',
      query: { enabled },
      authenticated: true,
    }),

  setPassword: (id: string, password: string) =>
    request<void>(`/api/v1/admin/users/${id}/password`, {
      method: 'PATCH',
      query: { password },
      authenticated: true,
    }),
}
